import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useCallback,
  useMemo,
} from 'react';
import {useIntl, defineMessages, FormattedMessage} from 'react-intl';
import {TextField, TextArea, view, FormFieldGroup, Link} from '@sail/ui';
import {FormStep} from './FormStep';
import {SubmitButton} from './SubmitButton';
import {validateEmail, validateMessage} from './validate';

export const messages = defineMessages({
  emailLabel: {
    id: 'ideal_inquiry.email_label',
    defaultMessage: 'Email address',
  },
  emailPlaceholder: {
    id: 'ideal_inquiry.email_placeholder',
    defaultMessage: 'name@example.com',
  },
  messageLabel: {
    id: 'ideal_inquiry.message_label',
    defaultMessage: 'Your message',
  },
  messagePlaceholder: {
    id: 'ideal_inquiry.message_placeholder',
    defaultMessage: 'Please describe your inquiry',
  },
  evidenceTitle: {
    id: 'ideal_inquiry.evidence_title',
    defaultMessage: 'Complete your inquiry',
  },
  submitInquiryButton: {
    id: 'ideal_inquiry.submit_inquiry_button',
    defaultMessage: 'Submit inquiry',
  },
  legalText: {
    id: 'ideal_inquiry.legal_text',
    defaultMessage:
      "By submitting this form, you agree to Stripe's <privacyPolicyLink>Privacy Policy</privacyPolicyLink>",
  },
});

interface EvidenceFormData {
  email: string;
  message: string;
}

interface SubmitEvidenceFormProps {
  initialEmail?: string;
  initialMessage?: string;
  handleSubmit: (formData: EvidenceFormData) => void;
  isPending?: boolean;
  isDisabled?: boolean;
  submissionError?: string | null;
  clearErrorOnInteraction: () => void;
}

export function SubmitEvidenceForm({
  initialEmail = '',
  initialMessage = '',
  handleSubmit,
  isPending = false,
  isDisabled = false,
  submissionError = null,
  clearErrorOnInteraction,
}: SubmitEvidenceFormProps) {
  const intl = useIntl();

  const [formData, setFormData] = useState<EvidenceFormData>({
    email: initialEmail,
    message: initialMessage,
  });

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof EvidenceFormData, string>>
  >({});

  // Validate form fields
  const validateFields = useCallback((): Partial<
    Record<keyof EvidenceFormData, string>
  > => {
    return {
      email: validateEmail(formData.email, intl),
      message: validateMessage(formData.message, intl),
    };
  }, [formData, intl]);

  const isButtonEnabled = useMemo(() => {
    return formData.email !== '' && formData.message !== '';
  }, [formData]);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    clearErrorOnInteraction();
    const {name, value} = event.target;
    setFormData((prev) => ({...prev, [name]: value}));
    if (name in formErrors) {
      setFormErrors((prevErrors) => ({...prevErrors, [name]: undefined}));
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors = validateFields();
    const hasErrors = Object.values(errors).some((err) => err !== undefined);

    if (hasErrors) {
      setFormErrors(errors);
      return;
    }

    handleSubmit(formData);
  };

  return (
    <FormStep>
      <view.form
        onSubmit={onSubmit}
        css={{stack: 'y', gap: 'medium', position: 'relative'}}
      >
        <view.div css={{stack: 'y', alignX: 'stretch', gap: 'small'}}>
          <view.span css={{font: 'label.large.emphasized'}}>
            {intl.formatMessage(messages.evidenceTitle)}
          </view.span>
        </view.div>

        <FormFieldGroup layout="vertical" error={submissionError}>
          <view.div
            css={{
              stack: 'y',
              alignX: 'stretch',
              gap: 'medium',
            }}
          >
            <TextField
              data-testid="email-input"
              type="email"
              name="email"
              label={intl.formatMessage(messages.emailLabel)}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              required
              value={formData.email}
              onChange={handleInputChange}
              error={formErrors.email}
              disabled={isDisabled}
              size="medium"
            />

            <TextArea
              data-testid="message-input"
              name="message"
              label={intl.formatMessage(messages.messageLabel)}
              placeholder={intl.formatMessage(messages.messagePlaceholder)}
              required
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              error={formErrors.message}
              disabled={isDisabled}
              size="medium"
            />

            <view.div
              css={{
                stack: 'y',
                alignX: 'stretch',
                gap: 'small',
              }}
            >
              <view.span css={{font: 'label.small', color: 'subdued'}}>
                <FormattedMessage
                  {...messages.legalText}
                  values={{
                    privacyPolicyLink: (chunks) => (
                      <Link
                        type="primary"
                        href="https://stripe.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        css={{fontWeight: 'inherit'}}
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </view.span>

              <SubmitButton
                data-testid="submit-inquiry-button"
                behavior="submit"
                pending={isPending}
                disabled={!isButtonEnabled || isDisabled}
                type="primary"
                size="medium"
              >
                {intl.formatMessage(messages.submitInquiryButton)}
              </SubmitButton>
            </view.div>
          </view.div>
        </FormFieldGroup>
      </view.form>
    </FormStep>
  );
}
